if (!customElements.get('quick-add-modal')) {
  /*
   * OTX (from Goons Up) performance: prefetch the product HTML on hover / touch / focus so
   * the quick-add popout opens (near) instantly instead of waiting on a fresh
   * network fetch at click time. Responses are cached for the session and
   * reused by show(). Falls back to a normal fetch if nothing was prefetched.
   */
  const quickAddPageCache = window.__quickAddPageCache || (window.__quickAddPageCache = new Map());
  const quickAddInFlight = window.__quickAddInFlight || (window.__quickAddInFlight = new Map());

  const quickAddFetchPage = (url) => {
    if (!url) return Promise.reject(new Error('Quick add: missing product URL'));
    if (quickAddPageCache.has(url)) return Promise.resolve(quickAddPageCache.get(url));
    if (quickAddInFlight.has(url)) return quickAddInFlight.get(url);

    const request = fetch(url)
      .then((response) => response.text())
      .then((text) => {
        quickAddPageCache.set(url, text);
        quickAddInFlight.delete(url);
        return text;
      })
      .catch((error) => {
        quickAddInFlight.delete(url);
        throw error;
      });

    quickAddInFlight.set(url, request);
    return request;
  };

  const quickAddOpenerFromEvent = (event) =>
    event.target.closest && event.target.closest('.quick-add__submit[data-product-url]');

  // Hover intent: wait ~100ms so sweeping the grid doesn't fire many requests.
  let quickAddHoverTimer;
  document.addEventListener(
    'pointerover',
    (event) => {
      const opener = quickAddOpenerFromEvent(event);
      if (!opener) return;
      const url = opener.getAttribute('data-product-url');
      clearTimeout(quickAddHoverTimer);
      quickAddHoverTimer = setTimeout(() => quickAddFetchPage(url).catch(() => {}), 100);
    },
    { passive: true }
  );
  document.addEventListener(
    'pointerout',
    (event) => {
      if (quickAddOpenerFromEvent(event)) clearTimeout(quickAddHoverTimer);
    },
    { passive: true }
  );

  // Touch / keyboard focus = clear intent, prefetch immediately.
  ['touchstart', 'focusin'].forEach((eventName) => {
    document.addEventListener(
      eventName,
      (event) => {
        const opener = quickAddOpenerFromEvent(event);
        if (opener) quickAddFetchPage(opener.getAttribute('data-product-url')).catch(() => {});
      },
      { passive: true }
    );
  });

  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        quickAddFetchPage(opener.getAttribute('data-product-url'))
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
